import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import { useDashboard, useTenantSlice } from "@/hooks/useDashboard";
import { MiniKPI, PriorityBadge } from "@/lib/dashboard";

/**
 * Cross-page tenant detail sheet.
 * Mounts at root layout level so any page can trigger it via setSelectedTenant.
 */
export default function TenantSheet() {
  const { data, selectedTenant, setSelectedTenant } = useDashboard();
  const slice = useTenantSlice(data, selectedTenant);

  return (
    <Sheet
      open={!!(slice && slice.client)}
      onOpenChange={(open) => !open && setSelectedTenant(null)}
    >
      {slice && slice.client && (
        <SheetContent>
          <Inner slice={{ ...slice, client: slice.client }} />
        </SheetContent>
      )}
    </Sheet>
  );
}

type Slice = NonNullable<ReturnType<typeof useTenantSlice>>;
type SliceWithClient = Omit<Slice, "client"> & { client: NonNullable<Slice["client"]> };

function Inner({ slice }: { slice: SliceWithClient }) {
  const c = slice.client;
  const totalMRR = slice.contracts.reduce((sum, k) => sum + (k.monthly_rate_native || 0), 0);
  const empty =
    !slice.contracts.length &&
    !slice.pending_approvals.length &&
    !slice.open_requests.length &&
    !slice.pending_inputs.length;

  return (
    <>
      <SheetHeader>
        <SheetTitle>{c.display_name}</SheetTitle>
        <SheetDescription>
          {c.slug} · {c.vertical || "—"} · {c.client_type} · {c.status}
        </SheetDescription>
      </SheetHeader>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-4 gap-2">
          <MiniKPI
            label="MRR"
            value={totalMRR > 0 ? `${c.currency_native} ${totalMRR.toLocaleString()}` : "—"}
          />
          <MiniKPI label="Approvals" value={slice.pending_approvals.length} />
          <MiniKPI label="Requests" value={slice.open_requests.length} />
          <MiniKPI label="Inputs" value={slice.pending_inputs.length} />
        </div>

        {slice.contracts.length > 0 && (
          <Block title={`Contracts · ${slice.contracts.length}`}>
            <ul className="text-sm divide-y divide-slate-800">
              {slice.contracts.map((k) => (
                <li key={k.id} className="py-2 flex justify-between">
                  <span className="text-slate-300">
                    {new Date(k.contract_start).toLocaleDateString()} · {k.status}
                  </span>
                  <span className="tabular-nums">
                    {k.currency_native} {k.monthly_rate_native?.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {slice.pending_approvals.length > 0 && (
          <Block title={`Pending Approvals · ${slice.pending_approvals.length}`}>
            <ul className="text-sm divide-y divide-slate-800">
              {slice.pending_approvals.map((a) => (
                <li key={a.id} className="py-2">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-slate-500">
                    {a.department} · T{a.tier} · {a.content_type}
                    {a.due_date && ` · due ${new Date(a.due_date).toLocaleDateString()}`}
                  </div>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {slice.open_requests.length > 0 && (
          <Block title={`Open Cross-Dept Requests · ${slice.open_requests.length}`}>
            <ul className="text-sm divide-y divide-slate-800">
              {slice.open_requests.map((r) => (
                <li key={r.id} className="py-2">
                  <div className="font-medium flex items-center gap-2">
                    {r.title}
                    <PriorityBadge priority={r.priority} />
                  </div>
                  <div className="text-xs text-slate-500">
                    {r.requester_department} → {r.assignee_department} · {r.status}
                  </div>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {slice.pending_inputs.length > 0 && (
          <Block title={`Awaiting CEO Inputs · ${slice.pending_inputs.length}`}>
            <ul className="text-sm divide-y divide-slate-800">
              {slice.pending_inputs.map((i) => (
                <li key={i.id} className="py-2">
                  <div className="font-medium">{i.title}</div>
                  <div className="text-xs text-slate-500">
                    {i.requester_department} · T{i.tier} · {i.priority}
                  </div>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {empty && (
          <div className="text-center py-12 text-slate-500 text-sm">
            No active items for this client.
          </div>
        )}
      </div>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
        {title}
      </h4>
      <Card className="px-3">{children}</Card>
    </section>
  );
}
